import React, { useState, useEffect } from 'react';
import type { Zone, ZoneType } from '../types.ts';
import { AVAILABLE_ZONING_MODELS } from '../types.ts';
import { ZONE_DEFINITIONS } from '../config.ts';

interface ZoningSectionProps {
  prompt: string;
  onPromptChange: (newPrompt: string) => void;
  model: string;
  onModelChange: (newModel: string) => void;
  onGenerateZones: (customRules?: string) => void;
  onClearZones: () => void;
  isZoning: boolean;
  zones: Zone[];
  aiExplanation?: string;
  errorMessage?: string;
  backendUrl: string;
  showZonesInPreview: boolean;
  onToggleShowZones: (show: boolean) => void;
  onFocusZone?: (zoneId: string | null) => void;
  focusedZoneId?: string | null;
}

const ZONING_PRESETS = [
  {
    label: '🛋️ Living + 🍽️ Dining',
    prompt: 'Partition the room into a primary living area with a large sofa and TV wall on the left side, and a dining zone with a 4-seat table on the right side.'
  },
  {
    label: '🛋️ Living + 💻 Workplace + 🌿 Relax',
    prompt: 'Create a spacious central living room, a quiet work corner with a desk and chair along the wall, and an intimate relaxation nook with an armchair by the window.'
  },
  {
    label: '🍳 Kitchen + 🍽️ Dining + 🛋️ Living',
    prompt: 'Open-concept layout: functional kitchen cooking area, directly adjacent dining space, and a distinct cozy living relaxation zone.'
  },
  {
    label: '🧸 Kids Room + 💻 Study',
    prompt: 'Children and student room: safe play and storage zone with soft rug, and an ergonomic study/workplace zone with a desk and shelving.'
  }
];

export function ZoningSection({
  prompt,
  onPromptChange,
  model,
  onModelChange,
  onGenerateZones,
  onClearZones,
  isZoning,
  zones,
  aiExplanation,
  errorMessage,
  backendUrl,
  showZonesInPreview,
  onToggleShowZones,
  onFocusZone,
  focusedZoneId
}: ZoningSectionProps) {
  // Rules editor state
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesContent, setRulesContent] = useState('');
  const [isSavingRules, setIsSavingRules] = useState(false);
  const [rulesSavedMessage, setRulesSavedMessage] = useState('');

  // Fetch current server rules
  useEffect(() => {
    fetch(`${backendUrl}/api/zoning/rules`)
      .then(res => res.json())
      .then(data => {
        if (data.content) {
          setRulesContent(data.content);
        }
      })
      .catch(err => console.warn('Could not load zoning rules:', err));
  }, [backendUrl]);

  const handleSaveRules = async () => {
    setIsSavingRules(true);
    setRulesSavedMessage('');
    try {
      const res = await fetch(`${backendUrl}/api/zoning/rules`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: rulesContent })
      });
      if (res.ok) {
        setRulesSavedMessage('Rules saved successfully to server!');
        setTimeout(() => setRulesSavedMessage(''), 4000);
      } else {
        throw new Error('Failed to save rules to server.');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSavingRules(false);
    }
  };

  const handleRunZoning = () => {
    onGenerateZones(rulesContent);
  };

  return (
    <section style={{
      backgroundColor: '#1e1e1e',
      border: '1px solid #333333',
      borderRadius: '12px',
      padding: '24px',
      marginTop: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #333333',
        paddingBottom: '14px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: 'rgba(59, 130, 246, 0.15)',
            color: '#60a5fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px'
          }}>
            📐
          </div>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#f3f4f6', margin: 0 }}>
              Intelligent Room Zoning
            </h2>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
              Gemini 3.7 Spatial Reasoning + Discriminator Voronoi Engine (/api/v2/zone)
            </div>
          </div>
        </div>

        {/* Action links */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setRulesOpen(!rulesOpen)}
            style={{
              backgroundColor: rulesOpen ? 'rgba(168, 85, 247, 0.2)' : 'rgba(255, 255, 255, 0.05)',
              border: '1px solid #444444',
              color: rulesOpen ? '#c084fc' : '#e5e7eb',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            title="Edit server architectural zoning rules Markdown"
          >
            📋 {rulesOpen ? 'Hide Zoning Rules' : 'Zoning Rules (Markdown)'}
          </button>

          {/* Open Documentation directly in a new tab */}
          <a
            href={`${backendUrl}/zoning-doc`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              color: '#93c5fd',
              padding: '6px 14px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              textDecoration: 'none',
              transition: 'all 0.2s'
            }}
            title="Open comprehensive ZONING.md documentation in a new browser tab"
          >
            📖 Zoning Documentation ↗
          </a>
        </div>
      </div>

      {/* Expandable Server Zoning Rules Editor */}
      {rulesOpen && (
        <div style={{
          backgroundColor: '#141414',
          border: '1px solid #2a2a2a',
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#f3f4f6' }}>
                📋 Server Zoning Architectural Guidelines (Markdown)
              </span>
              <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '10px' }}>
                Stored at <code>src/zoning/zoning-rules.md</code> & attached to every LLM prompt
              </span>
            </div>
            {rulesSavedMessage && (
              <span style={{ fontSize: '12px', color: '#10b981', fontWeight: 600 }}>
                ✓ {rulesSavedMessage}
              </span>
            )}
          </div>

          <textarea
            value={rulesContent}
            onChange={e => setRulesContent(e.target.value)}
            rows={8}
            style={{
              width: '100%',
              backgroundColor: '#0d0d0d',
              border: '1px solid #333333',
              borderRadius: '6px',
              color: '#e5e7eb',
              padding: '12px',
              fontSize: '12px',
              fontFamily: 'JetBrains Mono, monospace',
              lineHeight: '1.5',
              boxSizing: 'border-box'
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button
              type="button"
              onClick={handleSaveRules}
              disabled={isSavingRules}
              style={{
                backgroundColor: '#2563eb',
                border: 'none',
                color: '#ffffff',
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: isSavingRules ? 'wait' : 'pointer'
              }}
            >
              {isSavingRules ? 'Saving...' : '💾 Save Rules to Server'}
            </button>
          </div>
        </div>
      )}

      {/* Available Client Zones Chips */}
      <div>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#9ca3af', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Available Client Zones (rooms do not need to contain all):
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {Object.values(ZONE_DEFINITIONS).map(def => (
            <div
              key={def.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 12px',
                borderRadius: '16px',
                fontSize: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                border: `1px solid ${def.strokeColor}44`,
                color: '#e5e7eb'
              }}
              title={`${def.descriptionEn}\nTypical furniture: ${def.typicalFurniture.join(', ')}`}
            >
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: def.strokeColor
              }} />
              <span style={{ fontWeight: 500 }}>{def.nameEn}</span>
              <span style={{ fontSize: '10px', color: '#9ca3af' }}>({def.typicalFurniture.slice(0, 2).join(', ')}...)</span>
            </div>
          ))}
        </div>
      </div>

      {/* Prompt Input & Presets */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: '#e5e7eb' }}>
            Zoning Prompt & User Preferences:
          </label>

          {/* Model Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>Model:</span>
            <select
              value={model}
              onChange={e => onModelChange(e.target.value)}
              style={{
                backgroundColor: '#121212',
                border: '1px solid #333333',
                color: '#f3f4f6',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              {AVAILABLE_ZONING_MODELS.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick Presets */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {ZONING_PRESETS.map((preset, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => onPromptChange(preset.prompt)}
              style={{
                backgroundColor: '#121212',
                border: '1px solid #2a2a2a',
                color: '#9ca3af',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '11px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => (e.currentTarget.style.color = '#f3f4f6')}
              onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <textarea
          id="zoning-prompt-textarea"
          value={prompt}
          onChange={e => onPromptChange(e.target.value)}
          placeholder="Describe how you wish to partition the room into functional zones (e.g. Spacious living area with TV on the west wall, dining nook next to the kitchen)..."
          rows={3}
          style={{
            width: '100%',
            backgroundColor: '#121212',
            border: '1px solid #333333',
            borderRadius: '8px',
            color: '#f3f4f6',
            padding: '12px',
            fontSize: '13px',
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box'
          }}
        />

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              type="button"
              onClick={handleRunZoning}
              disabled={isZoning}
              style={{
                backgroundColor: isZoning ? '#1d4ed8' : '#2563eb',
                border: 'none',
                color: '#ffffff',
                padding: '10px 24px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: isZoning ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)',
                transition: 'all 0.2s'
              }}
            >
              {isZoning ? (
                <>
                  <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                  Generating & Computing Zones...
                </>
              ) : (
                <>
                  <span>⚡</span>
                  Generate Zones
                </>
              )}
            </button>

            {zones.length > 0 && (
              <button
                type="button"
                onClick={onClearZones}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #444444',
                  color: '#9ca3af',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Clear Zones
              </button>
            )}
          </div>

          {/* Toggle Zones in SVG Switch */}
          {zones.length > 0 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#e5e7eb' }}>
              <input
                type="checkbox"
                checked={showZonesInPreview}
                onChange={e => onToggleShowZones(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span>Show Zones in Room Layout Preview</span>
            </label>
          )}
        </div>
      </div>

      {/* Error display */}
      {errorMessage && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          color: '#fca5a5',
          fontSize: '13px'
        }}>
          <strong>Error:</strong> {errorMessage}
        </div>
      )}

      {/* Results Section: Active Zones Grid & AI Explanation */}
      {zones.length > 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          backgroundColor: '#171717',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #282828'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#f3f4f6' }}>
              Generated Zones ({zones.length}):
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af' }}>
              Click on any zone card to highlight its polygon in the preview
            </div>
          </div>

          {/* Zones Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            {zones.map(zone => {
              const isFocused = focusedZoneId === zone.id;
              return (
                <div
                  key={zone.id}
                  onClick={() => onFocusZone && onFocusZone(isFocused ? null : zone.id)}
                  style={{
                    backgroundColor: isFocused ? 'rgba(59, 130, 246, 0.15)' : '#222222',
                    border: `1px solid ${isFocused ? zone.strokeColor : '#333333'}`,
                    borderLeft: `4px solid ${zone.strokeColor}`,
                    borderRadius: '6px',
                    padding: '10px 14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: '#f3f4f6' }}>
                      {zone.name}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: zone.strokeColor,
                      backgroundColor: `${zone.strokeColor}18`,
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      {zone.areaPercent}%
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '12px', color: '#9ca3af' }}>
                    <span>Area: <strong style={{ color: '#e5e7eb' }}>{zone.area} m²</strong></span>
                    <span>Centroid: [{(zone.centroid[0] / 1000).toFixed(1)}, {(zone.centroid[1] / 1000).toFixed(1)}]m</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* AI Explanation */}
          {aiExplanation && (
            <div style={{
              backgroundColor: 'rgba(59, 130, 246, 0.06)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '6px',
              padding: '12px 14px',
              fontSize: '13px',
              color: '#dbeafe',
              lineHeight: '1.6'
            }}>
              <strong>💡 Architect Reasoning (Gemini):</strong> {aiExplanation}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
